import React, { useMemo, useState } from 'react';

/**
 * Spoiler component
 * - Initially hides its children with a visual obfuscation.
 * - Reveals content on mouse click or keyboard (Enter/Space) for accessibility.
 * - Gracefully renders empty/null values as-is without interactivity.
 */
/**
 * Props
 * - variant: 'basic' | 'frosted' | 'candyCane' | 'snow' (default: 'basic')
 */
export default function Spoiler({ children, variant = 'candyCane' }) {
  const [revealed, setRevealed] = useState(false);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // If there is nothing to show, render as-is
  if (children === undefined || children === null || children === '') {
    return <span>{children}</span>;
  }

  // Base style shared by both states (needed so transitions can animate smoothly)
  const baseStyle = useMemo(() => ({
    transition: prefersReducedMotion
      ? 'none'
      : 'color 200ms ease, text-shadow 200ms ease, background-color 200ms ease, filter 200ms ease, box-shadow 200ms ease',
    borderRadius: 6,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-block',
    maxWidth: '100%',
    padding: '0 6px',
    outline: 'none',
  }), [prefersReducedMotion]);

  // Obscured state by variant
  const hiddenStyle = useMemo(() => {
    const common = { color: 'transparent' };
    switch (variant) {
      case 'frosted':
        return {
          ...common,
          textShadow: '0 0 10px rgba(0,0,0,0.9)',
          backgroundColor: 'rgba(255,255,255,0.35)',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4), inset 0 8px 20px rgba(0,0,0,0.12)',
          filter: prefersReducedMotion ? 'none' : 'blur(4px)',
        };
      case 'candyCane':
        return {
          ...common,
          textShadow: '0 0 8px rgba(0,0,0,0.9)',
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(220,20,60,0.22) 0 8px, rgba(255,255,255,0.32) 8px 16px)',
          backgroundColor: 'rgba(255,255,255,0.2)',
          filter: prefersReducedMotion ? 'none' : 'blur(3px)',
        };
      case 'snow':
        return {
          ...common,
          textShadow: '0 0 8px rgba(0,0,0,0.9)',
          backgroundImage: [
            'radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)',
            'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)'
          ].join(', '),
          backgroundSize: '12px 12px, 18px 18px',
          backgroundPosition: '0 0, 6px 6px',
          backgroundColor: 'rgba(173,216,230,0.15)',
          filter: prefersReducedMotion ? 'none' : 'blur(2px)',
        };
      case 'basic':
      default:
        return {
          ...common,
          textShadow: '0 0 8px rgba(0,0,0,0.9)',
          backgroundColor: 'rgba(0,0,0,0.08)'
        };
    }
  }, [variant, prefersReducedMotion]);

  // Revealed state
  const revealedStyle = useMemo(() => ({
    color: 'inherit',
    textShadow: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    filter: 'none',
  }), []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      setRevealed(prev => !prev);
    } else if (e.key === 'Escape') {
      setRevealed(false);
    }
  };

  return (
    <span
      onClick={() => setRevealed(prev => !prev)}
      onKeyDown={handleKeyDown}
      role="button"
      aria-pressed={revealed}
      tabIndex={0}
      title={revealed ? 'Click to hide' : 'Click to reveal'}
      style={{
        ...baseStyle,
        ...(revealed ? revealedStyle : hiddenStyle)
      }}
      onFocus={(e) => {
        const el = e.currentTarget;
        el.style.boxShadow = '0 0 0 3px rgba(30,144,255,0.35)';
      }}
      onBlur={(e) => {
        const el = e.currentTarget;
        el.style.boxShadow = (revealed ? revealedStyle.boxShadow : hiddenStyle.boxShadow) || 'none';
      }}
    >
      {children}
    </span>
  );
}
